import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { handleDownloadPDF } from '../../utils/pdfDownload';
import {
  IconButton,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Box,
  Toolbar,
  Container,
  Paper,
  useTheme,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import {
  Mic,
  MicOff,
  ArrowDownToLine,
  FilePenLine,
  Copy,
  MessageSquareMore,
  X,
} from 'lucide-react';
import { triggerNewSummary } from '../../utils/axiosApiRequest';
import {
  fetchAudioFile,
  updateConversationSummary,
} from '../../utils/axiosApiRequest';
import { TailSpin } from 'react-loader-spinner';
import MobileToolbar from './MobileToolbar';
import MobileAudioPlayer from './MobileAudioPlayer';
import MobileRecording from './MobileRecording';
import MobileTranscript from './MobileTranscript';
import MobileEdit from './MobileEdit';
import SummaryRecordingIcon from './SummaryRecordingIcon';
import MarkdownRenderer from './MarkdownRenderer';

const Summary = ({
  summary,
  setSummary,
  summaryId,
  setSummaryId,
  setGeneratingSummary,
  audioFilename,
  conversation,
  promptTemplates,
  selectedPrompt,
  setSelectedPrompt,
  startRecording,
  stopRecording,
  recording,
  audioReady,
  recordingDuration,
  wordCount,
  messages,
  generatingSummary,
  isMobile = false,
}) => {
  const [audioUrl, setAudioUrl] = useState('');
  const [download, setDownload] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [draftSummary, setDraftSummary] = useState(summary);
  const [audioDuration, setAudioDuration] = useState(0);
  const [showMessages, setShowMessages] = useState(false);
  const [showMobileAudioPlayer, setShowMobileAudioPlayer] = useState(false);
  const [showMobileRecording, setShowMobileRecording] = useState(false);
  const [showMobileTranscript, setShowMobileTranscript] = useState(false);
  const [showMobileEdit, setShowMobileEdit] = useState(false);
  const [continuousDuration, setContinuousDuration] = useState(0);
  const durationIntervalRef = useRef(null);

  const { t, i18n } = useTranslation();
  const summaryRef = useRef(null);
  const textFieldRef = useRef(null);
  const audioElementRef = useRef(null);
  const theme = useTheme();

 
  const isApproved = conversation?.status === 'APPROVED';
 
  const handleSave = useCallback(async (id, selectedPrompt) => {
    try {
      const payload = {
        prompt_template_id: selectedPrompt,
        summary: draftSummary,
      };
      await updateConversationSummary(id, payload);
      setSummary(draftSummary);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating summary:', error);
    }
  }, [draftSummary, setSummary]);

  const copyToClipboard = useCallback((msg) => {
    navigator.clipboard.writeText(msg).catch(() => alert('Failed to copy!'));
  }, []);

  const regenerateSummary = useCallback(async (id, selectedPrompt) => {
    try {
      setSummary(null);
      setGeneratingSummary(id);
      const data = await triggerNewSummary(id, {
        prompt_template_id: selectedPrompt,
      });
      const newestSummary = data.summaries.reduce((latest, current) => {
        return current.unix_timestamp > (latest?.unix_timestamp || 0)
          ? current
          : latest;
      }, null);

      if (newestSummary) {
        setSummary(newestSummary.summary);
        setSummaryId(newestSummary.id);
        if (newestSummary.prompt_template) {
          setSelectedPrompt(newestSummary.prompt_template.id);
        } else {
          setSelectedPrompt('');
        }
      } else {
        setSummary('');
      }
      setGeneratingSummary(null);
    } catch (error) {
      console.error('Error fetching new summary:', error);
      setGeneratingSummary(null);
    }
  }, [setGeneratingSummary, setSummary, setSummaryId, setSelectedPrompt]);

  const formatDuration = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const changeLanguage = useCallback((lng) => {
    i18n.changeLanguage(lng);
  }, [i18n]);

  // Start counting duration when recording starts
  useEffect(() => {
    if (recording) {
      setContinuousDuration(audioDuration);

      // Clear any existing interval
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }

      durationIntervalRef.current = setInterval(() => {
        setContinuousDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }

    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [recording, audioDuration]);

  // Reset audio URL when audioFilename changes
  useEffect(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl('');
    }

    if (audioFilename && audioReady) {
      const filename = audioFilename.split(/[/\\]/).pop();
      setDownload(filename);
      fetchAudioFile(filename)
        .then((response) => {
          const blobUrl = URL.createObjectURL(response.data);
          setAudioUrl(blobUrl);
        })
        .catch((error) => {
          console.error('Error fetching audio file:', error);
        });
    }
    summaryRef.current?.scrollIntoView({ behavior: 'smooth' });

    // Cleanup function to revoke object URL when component unmounts or audioFilename changes
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioFilename, audioReady]);

  useEffect(() => {
    setDraftSummary(summary);
  }, [summary]);

  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.style.height = 'auto'; // Reset height
      textFieldRef.current.style.height = `${textFieldRef.current.scrollHeight}px`; // Adjust to content
    }
  }, [draftSummary, isEditing]);

  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isEditing]);

  // Add event listener for ESC key to cancel editing
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isEditing) {
        setIsEditing(false);
        setDraftSummary(summary);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, summary]);

  // Mobile toolbar handlers - memoized with useCallback
  const handlePlayClick = useCallback(() => setShowMobileAudioPlayer(true), []);
  const handleMicClick = useCallback(() => setShowMobileRecording(true), []);
  const handleEditClick = useCallback(() => setShowMobileEdit(true), []);
  const handleMessageClick = useCallback(() => setShowMobileTranscript(true), []);

  // Close handlers - memoized with useCallback
  const handleCloseAudioPlayer = useCallback(() => setShowMobileAudioPlayer(false), []);
  const handleCloseRecording = useCallback(() => setShowMobileRecording(false), []);
  const handleCloseTranscript = useCallback(() => setShowMobileTranscript(false), []);
  const handleCancelEdit = useCallback(() => setShowMobileEdit(false), []);

  const handleCopyToClipboard = useCallback(() => {
    copyToClipboard(summary);
  }, [copyToClipboard, summary]);

  const handleRegenerateSummary = useCallback((id, promptTemplateId) => {
    regenerateSummary(id, promptTemplateId);
  }, [regenerateSummary]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'none' }}>
        <audio
          key={audioFilename}
          ref={audioElementRef}
          src={audioUrl}
          onLoadedMetadata={() => {
            if (audioElementRef.current) {
              setAudioDuration(Math.round(audioElementRef.current.duration));
            }
          }}
        />
      </Box>
      {/*//! Component Header */}
      {!isMobile && (
        <Box
          sx={{
            width: '100%',
            height: '68px',
            borderBottom: '1px solid #e0e0e0',
            zIndex: 10,
            bgcolor: 'background.paper',
          }}
        >
          <Toolbar
            sx={{
              px: { md: 6, xl: 2},
            }}
          >
            <Typography variant='h2' component='h2' sx={{ flexGrow: 1 }}>
              {t('summary.title')}
            </Typography>

            {/* Language Selector Dropdown */}
            <FormControl size='small' sx={{ minWidth: 100, mr: 2 }}>
              <Select
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                variant='standard'
                disableUnderline
                sx={{
                  '& .MuiSelect-select': {
                    paddingBottom: 0,
                    fontSize: 14,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem value='en'>{t('languages.en')}</MenuItem>
                <MenuItem value='de'>{t('languages.de')}</MenuItem>
              </Select>
            </FormControl>

            {/* <IconButton aria-label='theme'>
              <Brightness4Icon />
            </IconButton> */}
          </Toolbar>
        </Box>
      )}

      {/*//! Summary Tool Bar - Only show on desktop */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', xl: '1024px' },
              justifyContent: 'space-between',
              paddingBlock: 1.4,
              paddingInline: { md: 6, xl: 0 },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              zIndex: 9,
              position: 'relative',
              opacity: recording ? 0.6 : 1,
              pointerEvents: recording ? 'none' : 'auto',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                width: { xs: '100%', xl: 'auto' },
              }}
            >
              <FormControl
                sx={{
                  minWidth: 200,
                  width: { xs: '100%', xl: 'auto' },
                }}
                size='small'
                disabled={generatingSummary || isEditing || isApproved}
              >
                <Select
                  value={selectedPrompt}
                  onChange={(e) => setSelectedPrompt(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {promptTemplates &&
                    promptTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.template_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Button
                variant='buttonStandard'
                startIcon={<CachedIcon />}
                onClick={() =>
                  handleRegenerateSummary(conversation.id, selectedPrompt)
                }
                sx={{ width: { xs: '100%', xl: 'auto' } }}
                disabled={generatingSummary || isEditing || isApproved}
              >
                {t('summary.regenerateSummary')}
              </Button>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: { xs: '100%', xl: 'auto' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Button
                  variant='outlined'
                  startIcon={<Copy strokeWidth={1.25} size={20} />}
                  onClick={handleCopyToClipboard}
                  size='small'
                  disabled={generatingSummary || isEditing || !summary}
                >
                  {t('common.copy')}
                </Button>
                <Button
                  variant='outlined'
                  startIcon={<ArrowDownToLine strokeWidth={1.25} size={20} />}
                  onClick={() => handleDownloadPDF(summary, conversation)}
                  size='small'
                  disabled={generatingSummary || isEditing || !summary}
                >
                  {t('summary.downloadPDF')}
                </Button>
                <Button
                  variant='outlined'
                  startIcon={<FilePenLine strokeWidth={1.25} size={20} />}
                  onClick={() => setIsEditing(true)}
                  size='small'
                  disabled={isEditing || generatingSummary || !summary || isApproved}
                >
                  {t('common.edit')}
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
              >
                <Button
                  variant='cancelButton'
                  onClick={() => setIsEditing(false)}
                  size='small'
                  disabled={!isEditing}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant='saveButton'
                  onClick={() => handleSave(conversation.id, selectedPrompt)}
                  size='small'
                  color='primary'
                  disabled={!isEditing}
                >
                  {t('common.save')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/*//! Summary */}
      <Box
        ref={summaryRef}
        sx={{
          width: '100%',
          flexGrow: 1,
          overflowY: 'scroll',
          height: 0,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: '#F9F9F9',
        }}
      >
        <Container
          maxWidth='false'
          disableGutters={true}
          sx={{
            maxWidth: { md: '1200px', xl: '1024px' },
            minHeight: isMobile ? 'auto' : '1460px',
          }}
        >
          {isEditing ? (
            <TextField
              value={draftSummary || ''}
              onChange={(e) => setDraftSummary(e.target.value)}
              multiline
              fullWidth
              variant='outlined'
              inputRef={textFieldRef}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: { md: 6, xl: 10 },
                  minHeight: '1460px',
                  alignItems: 'flex-start',
                  '& fieldset': {
                    borderColor: theme.palette.secondary.main,
                    borderWidth: 2,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.secondary.main,
                  },
                  '& textarea': {
                    alignSelf: 'flex-start',
                  },
                },
                mt: 4,
                backgroundColor: 'white',
                borderRadius: 2,
              }}
              placeholder={t('summary.pressEscToCancel')}
              InputProps={{
                sx: {
                  alignItems: 'flex-start',
                  '& .MuiInputBase-input': {
                    verticalAlign: 'top',
                  },
                },
              }}
            />
          ) : (
            <Paper
              sx={{
                mt: { xs: 0, xl: 4 },
                px: { xs: 3, md: 6, xl: 12 },
                py: { xs: 3, md: 4, xl: 8 },
                pb: { xs: 10 },
                borderRadius: 1,
                fontFamily: 'Geist Mono',
                minHeight: isMobile ? '100vh' : '1400px',
                backgroundColor: '#ffffff',
                boxShadow: {
                  xs: 'none',
                  lg: '0px 0px 20px rgba(0, 0, 0, 0.1)',
                },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: generatingSummary ? 'center' : 'stretch',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                width: '100%',
                overflowX: 'hidden',
              }}
            >
              {generatingSummary ? (
                <Box
                  sx={{
                    mt: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TailSpin
                    height='40'
                    width='40'
                    color={theme.palette.secondary.main}
                  />
                  <Typography sx={{ mt: 4, whiteSpace: 'pre-line', textAlign: 'center' }}>
                    {t('summary.generating')}
                  </Typography>
                </Box>
              ) : summary ? (
                <MarkdownRenderer content={summary} />
              ) : (
                <Typography variant='body1'>
                  {t('summary.noSummaryAvailable')}
                </Typography>
              )}
            </Paper>
          )}
        </Container>

        {/* Message icon with floating dialog - Only on desktop */}
        {!isMobile && (
          <Box
            sx={{
              position: 'fixed',
              bottom: { md: 150, lg: 150, xl: 150, xxl: 120 },
              right: { md: 40, lg: 40, xl: 100, xxl: 80 },
              zIndex: 8,
            }}
          >
            <IconButton
              disableRipple
              disableFocusRipple
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              onClick={() => setShowMessages((prev) => !prev)}
            >
              <MessageSquareMore
                strokeWidth={1.4}
                size={54}
                color={'white'}
                fill={
                  !showMessages
                    ? theme.palette.primary.main
                    : theme.palette.secondary.main
                }
              />
            </IconButton>

            {/* Messages Dialog - Positioned relative to icon */}
            {showMessages && (
              <Paper
                elevation={0}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 70,
                  width: '500px',
                  maxHeight: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  zIndex: 1000,
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 0px 10px 2px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #E4E4E7',
                }}
                onClick={(e) => e.stopPropagation()} // Prevent clicks from reaching parent elements
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 22px',
                    borderBottom: '1px solid #E4E4E7',
                  }}
                >
                  <Typography variant='h2' component='h2'>
                    {t('summary.transcript')}
                  </Typography>
                  <IconButton
                    onClick={() => setShowMessages(false)}
                    size='small'
                    sx={{
                      padding: 0,
                    }}
                  >
                    <X strokeWidth={2} size={24} />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    padding: 2,
                    overflowY: 'auto',
                    backgroundColor: '#FFFFFF',
                    maxHeight: '500px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {messages && messages.length > 0 ? (
                    messages.map((message, index) => (
                      <Box
                        key={index}
                        sx={{
                          padding: '8px 12px',
                          backgroundColor: '#F4F4F5',
                          borderRadius: '4px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          alignSelf: 'flex-start',
                          maxWidth: '100%',
                        }}
                      >
                        <Typography
                          variant='body2'
                          sx={{
                            fontFamily: 'Geist',
                            fontWeight: 400,
                            fontSize: 14,
                            lineHeight: 1.3,
                            color: '#3D3D3D',
                            wordBreak: 'break-word',
                          }}
                        >
                          {message}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography
                      variant='body1'
                      sx={{
                        textAlign: 'center',
                        py: 4,
                        fontFamily: 'Geist',
                        color: '#3D3D3D',
                      }}
                    >
                      {t('summary.noMessagesAvailable')}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      {/*//! Recording Toolbar - Fixed at bottom */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            borderTop: '1px solid #e0e0e0',
            px: '48px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBlock: 2,
              gap: 2,
              flexWrap: 'wrap',
              zIndex: 9,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: { md: '100%', xxl: '400px' },
              }}
            >
              <audio
                key={audioFilename}
                ref={audioElementRef}
                controls
                style={{ maxWidth: '100%', height: '30px', flexGrow: 1 }}
                src={audioUrl}
                controlsList='nodownload'
                onLoadedMetadata={() => {
                  if (audioElementRef.current) {
                    setAudioDuration(
                      Math.round(audioElementRef.current.duration)
                    );
                  }
                }}
                disabled={recording}
              />
              <a href={audioUrl} download={download}>
                <IconButton
                  aria-label={t('common.download')}
                  sx={{ ml: 1 }}
                  disabled={recording || !audioUrl}
                >
                  <ArrowDownToLine strokeWidth={2} size={20} />
                </IconButton>
              </a>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { md: 1, lg: 2 },
                flexWrap: 'wrap',
              }}
            >
              <FormControl
                sx={{
                  minWidth: { md: '180px', lg: '200px' },
                  bgcolor: '#ffff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    borderColor: '#E4E4E7',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E4E4E7',
                  },
                }}
                size='small'
                disabled={generatingSummary || isEditing || isApproved}
              >
                <Select
                  value={selectedPrompt}
                  onChange={(e) => setSelectedPrompt(e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {promptTemplates &&
                    promptTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.template_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Button
                variant='buttonStandard'
                startIcon={
                  recording ? (
                    <MicOff strokeWidth={2} size={20} />
                  ) : (
                    <Mic strokeWidth={2} size={20} />
                  )
                }
                onClick={recording ? stopRecording : startRecording}
                sx={{
                  height: '40px',
                  bgcolor: recording
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main,
                }}
                disabled={generatingSummary || isEditing || (isApproved && !recording)}
              >
                {recording
                  ? t('recording.stopRecording')
                  : t('recording.resumeRecording')}
              </Button>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { md: 2, lg: 4, xl: 6 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Geist Mono',
                    fontSize: {md: 18, lg: 24},
                    fontWeight: 300,
                    letterSpacing: '1.67%',
                    color: '#000000',
                    lineHeight: 1.2,
                  }}
                >
                  {wordCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: theme.palette.primary.light,
                  }}
                >
                  {t('recording.words')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Geist Mono',
                    fontSize: {md: 18, lg: 24},
                    fontWeight: 300,
                    letterSpacing: '1.67%',
                    color: '#000000',
                    lineHeight: 1.2,
                  }}
                >
                  {formatDuration(
                    recording
                      ? continuousDuration
                      : audioDuration || recordingDuration
                  )}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: theme.palette.primary.light,
                  }}
                >
                  {t('recording.recordingTime')}
                </Typography>
              </Box>
              <SummaryRecordingIcon recording={recording} />
            </Box>
          </Box>
        </Box>
      )}

      {/* Mobile UI Components */}
      {isMobile &&
        !showMobileAudioPlayer &&
        !showMobileRecording &&
        !showMobileTranscript &&
        !showMobileEdit && (
          <MobileToolbar
            onPlayClick={handlePlayClick}
            onMicClick={handleMicClick}
            onEditClick={handleEditClick}
            onMessageClick={handleMessageClick}
            hasAudio={!!audioUrl}
          />
        )}

      {isMobile && showMobileAudioPlayer && audioUrl && (
        <MobileAudioPlayer
          audioUrl={audioUrl}
          audioFilename={audioFilename}
          onClose={handleCloseAudioPlayer}
          formatDuration={formatDuration}
          audioDuration={audioDuration}
          audioElementRef={audioElementRef}
        />
      )}

      {isMobile && showMobileRecording && (
        <MobileRecording
          onClose={handleCloseRecording}
          recording={recording}
          startRecording={startRecording}
          stopRecording={stopRecording}
          recordingDuration={recordingDuration}
          wordCount={wordCount}
          promptTemplates={promptTemplates}
          selectedPrompt={selectedPrompt}
          setSelectedPrompt={setSelectedPrompt}
          audioDuration={audioDuration}
        />
      )}

      {isMobile && showMobileTranscript && (
        <MobileTranscript onClose={handleCloseTranscript} messages={messages} />
      )}

      {isMobile && showMobileEdit && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 56, // Leave space for the MobileLayout toolbar
            backgroundColor: 'background.paper',
            zIndex: 1100, // Lower than MobileTranscript but higher than most content
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <MobileEdit
            summary={summary}
            draftSummary={draftSummary}
            setDraftSummary={setDraftSummary}
            onSave={handleSave}
            onCancel={handleCancelEdit}
            summaryId={summaryId}
            selectedPrompt={selectedPrompt}
            setSelectedPrompt={setSelectedPrompt}
            promptTemplates={promptTemplates}
            onCopy={handleCopyToClipboard}
            onDownload={() => handleDownloadPDF(summary, conversation)}
            onRegenerate={handleRegenerateSummary}
            onClose={handleCancelEdit}
            conversation={conversation}
            generatingSummary={generatingSummary}
          />
        </Box>
      )}
    </Box>
  );
};

// Wrap the component with React.memo to prevent unnecessary re-renders
export default React.memo(Summary);
